import { useState, useEffect } from 'react';

interface BookCallModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function BookCallModal({ isOpen, onClose }: BookCallModalProps) {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    industry: '',
    revenue: '',
    phone: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitted(true);
    setSubmitting(false);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm" />

      {/* Modal */}
      <div className="relative z-10 w-full max-w-md bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden">
        {/* Header */}
        <div className="px-7 pt-7 pb-6 border-b border-white/5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3 className="font-display font-bold text-white text-xl mb-1">
                Book Your Free Strategy Call
              </h3>
              <p className="text-white/35 text-sm">
                We only take 4 new clients/month. Spots fill fast.
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center text-white/30 hover:text-white transition-colors cursor-pointer flex-shrink-0 mt-0.5"
            >
              <i className="ri-close-line text-xl" />
            </button>
          </div>
        </div>

        {submitted ? (
          <div className="px-7 py-12 text-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-electric-blue/10 border border-electric-blue/30 mx-auto mb-5">
              <i className="ri-check-line text-electric-blue text-2xl" />
            </div>
            <h4 className="font-display font-bold text-white text-lg mb-2">You&apos;re In.</h4>
            <p className="text-white/40 text-sm leading-relaxed">
              Our team will reach out within 24 hours to confirm your strategy session.
              Check your inbox — this is where your 3X journey starts.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="px-7 py-6 flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/40 text-xs font-medium mb-1.5 block">Full Name *</label>
                <input
                  type="text"
                  name="name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="John Smith"
                  className="w-full bg-white/4 border border-white/8 rounded-md px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-electric-blue/40 transition-colors"
                />
              </div>
              <div>
                <label className="text-white/40 text-xs font-medium mb-1.5 block">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 (555) 000-0000"
                  className="w-full bg-white/4 border border-white/8 rounded-md px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-electric-blue/40 transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="text-white/40 text-xs font-medium mb-1.5 block">Work Email *</label>
              <input
                type="email"
                name="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@company.com"
                className="w-full bg-white/4 border border-white/8 rounded-md px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-electric-blue/40 transition-colors"
              />
            </div>

            <div>
              <label className="text-white/40 text-xs font-medium mb-1.5 block">Company Name *</label>
              <input
                type="text"
                name="company"
                required
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                placeholder="Your company name"
                className="w-full bg-white/4 border border-white/8 rounded-md px-3 py-2.5 text-white text-sm placeholder-white/20 focus:outline-none focus:border-electric-blue/40 transition-colors"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/40 text-xs font-medium mb-1.5 block">Industry</label>
                <select
                  name="industry"
                  value={form.industry}
                  onChange={(e) => setForm({ ...form, industry: e.target.value })}
                  className="w-full bg-white/4 border border-white/8 rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/40 transition-colors cursor-pointer"
                >
                  <option value="" className="bg-black">Select...</option>
                  <option value="automotive" className="bg-black">Automotive</option>
                  <option value="real-estate" className="bg-black">Real Estate</option>
                  <option value="legal" className="bg-black">Legal</option>
                  <option value="entertainment" className="bg-black">Entertainment</option>
                </select>
              </div>
              <div>
                <label className="text-white/40 text-xs font-medium mb-1.5 block">Monthly Revenue</label>
                <select
                  name="revenue"
                  value={form.revenue}
                  onChange={(e) => setForm({ ...form, revenue: e.target.value })}
                  className="w-full bg-white/4 border border-white/8 rounded-md px-3 py-2.5 text-white text-sm focus:outline-none focus:border-electric-blue/40 transition-colors cursor-pointer"
                >
                  <option value="" className="bg-black">Select...</option>
                  <option value="50k-100k" className="bg-black">$50K – $100K</option>
                  <option value="100k-500k" className="bg-black">$100K – $500K</option>
                  <option value="500k-1m" className="bg-black">$500K – $1M</option>
                  <option value="1m+" className="bg-black">$1M+</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="whitespace-nowrap mt-2 w-full py-3.5 bg-electric-blue text-black font-bold text-sm rounded-md glow-blue transition-all duration-300 cursor-pointer disabled:opacity-70"
            >
              {submitting ? 'Booking...' : 'Claim My Free Strategy Call →'}
            </button>

            <p className="text-white/18 text-xs text-center">
              No spam. No hard sells. Just an honest conversation about your growth.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
